import type { BabelLang, CardMenuItem, CardStatus } from '../../shared/protocol';
import { bt, btCard } from '../../shared/babel/strings';
import { CardIcon } from './CardIcon';

const PHOTO_TARGET: Record<string, string> = {
  photoUp: 'UP',
  photoMailbox: 'MAILBOX',
  photoHouse: 'HOUSE',
  photoFeet: 'FEET',
  photoBuilding: 'TOWER',
  photoSelfie: 'SELFIE', // v13 BOLT-036: v11 §2C card was falling back to generic 'PHOTO'
};

export function cardTitle(c: CardMenuItem, lang: BabelLang | null = null): string {
  if (c.script) return (c.label || 'CARD').toUpperCase().slice(0, 14); // v8 community card
  // v13 BOLT-036: three cards share family 'direction' (Direction/Quadrants/
  // Borders, v11 §2K/§2L) — the family is no longer a title, the card's own
  // name is. v17 BOLT-074: those NAMES translate too (Jake's scope override
  // over v16's "names stay English"); every other family takes its family word.
  if (c.family === 'direction') return btCard(lang, c.id, 'name', c.label.toUpperCase());
  if (c.family === 'airhorn') return bt(lang, 'airhorn'); // v16 BOLT-072
  // v24, revised in the field: the title is the FAMILY word ("NEAREST") and the
  // feature is the data line ("PLAYGROUND"). Jake: "Maybe the card name is
  // 'Nearest' and the subtext is 'Playground'." It also stops the long names
  // ("NEAREST PLAYGROUND") truncating in the rail.
  if (c.family === 'landmark') return bt(lang, 'nearest');
  return c.family === 'radar' ? bt(lang, 'radar') : c.family === 'photo' ? bt(lang, 'photo') : bt(lang, 'thermo');
}
// BOLT-036: per-card cut hints for the direction family (was one '4-WAY').
const DIRECTION_DATA: Record<string, string> = {
  direction: 'X-CUT',
  quadrants: 'CROSS',
  borders: '25/75',
};
// v24 BOLT-118: the feature class each landmark card cuts by.
// Jake (2026-07-29): spell them out. An abbreviation saves a few pixels and
// costs a beat of "wait, PLAY what?" mid-round. FOOD stays: it is shorter than
// "restaurant" AND more accurate, since the card counts cafes and takeaways too.
const LANDMARK_DATA: Record<string, string> = {
  nearestPark: 'PARK',
  nearestRestaurant: 'FOOD',
  nearestPlayground: 'PLAYGROUND',
  nearestSchool: 'SCHOOL',
  nearestWater: 'WATER',
  nearestBridge: 'BRIDGE',
};
/** v17 BOLT-074: the orange data line translates too — the target WORDS and
 *  the ft/km units. The digits beside them stay latin (numbers never scramble). */
export function cardData(c: CardMenuItem, lang: BabelLang | null = null): string {
  if (c.script) return 'COMMUNITY';
  if (c.family === 'direction') return btCard(lang, c.id, 'data', DIRECTION_DATA[c.id] ?? '4-WAY');
  if (c.family === 'photo') return btCard(lang, c.id, 'data', PHOTO_TARGET[c.id] ?? 'PHOTO');
  if (c.family === 'airhorn') return btCard(lang, c.id, 'data', 'FULL BLAST'); // v16 BOLT-072
  // v24 BOLT-118: the data line is the feature CLASS the card cuts by.
  if (c.family === 'landmark') return btCard(lang, c.id, 'data', LANDMARK_DATA[c.id] ?? 'NEAREST');

  const range = c.radiusFt ?? c.minFt ?? 0;
  // long-range radars read "1 KM", not "3281 FT"
  return range >= 3281 ? `${Math.round(range / 3280.84)} ${bt(lang, 'km')}` : `${range} ${bt(lang, 'ft')}`;
}

// ============================================================
// v15 BOLT-052 — the physical-deck metaphor: `playsTotal: N` renders N
// TILES (spent + in-flight + playable), replacing the 'N left' counter.
// Pure derivation, exported for tests; iOS ports it in Format.railInstances
// (parity XCTest). The BOLT-042 veto ✗ rides exactly ONE spent instance —
// the most recently spent — never a playable sibling.
// ============================================================
export interface CardInstance {
  k: number; // stable per-card index (spent first, then in-flight, then playable)
  status: CardStatus;
  vetoed: boolean;
  /** carries the base `card-<id>` testid (playable first; else the tail) */
  primary: boolean;
}

export function expandInstances(c: CardMenuItem): CardInstance[] {
  const total = Math.max(1, c.playsTotal ?? 1);
  // legacy/community cards carry no playsLeft: played ⇒ 0, anything else ⇒ 1
  const left = Math.min(total, Math.max(0, c.playsLeft ?? (c.status === 'played' ? 0 : 1)));
  const spent = total - left;
  const inFlight = c.status === 'playing' ? 1 : 0;
  const rest = Math.max(0, left - inFlight);
  const restStatus: CardStatus = c.status === 'disabled' ? 'disabled' : 'available';
  const out: CardInstance[] = [];
  for (let i = 0; i < spent; i++) {
    out.push({ k: i, status: 'played', vetoed: !!c.vetoed && i === spent - 1, primary: false });
  }
  if (inFlight) out.push({ k: spent, status: 'playing', vetoed: false, primary: true });
  for (let i = 0; i < rest; i++) {
    out.push({ k: spent + inFlight + i, status: restStatus, vetoed: false, primary: !inFlight && i === 0 });
  }
  if (!out.some((i) => i.primary)) out[out.length - 1].primary = true; // fully spent: the tail answers for the card
  return out;
}

function instanceState(c: CardMenuItem, status: CardStatus, lang: BabelLang | null): string {
  if (status === 'played') return bt(lang, 'played');
  if (status === 'playing') return c.family === 'photo' ? bt(lang, 'awaiting') : bt(lang, 'running');
  if (status === 'disabled') return c.disabledReason ?? bt(lang, 'locked');
  return bt(lang, 'ready');
}

/** Horizontal, scrollable carousel of the 8 seeker cards (§4.8.1). */
export function CardRail({
  cards,
  onPick,
  locked = false,
  lang = null,
}: {
  cards: CardMenuItem[];
  onPick: (c: CardMenuItem) => void;
  locked?: boolean;
  /** v16 BOLT-071: the seeker's active Babel language (null = English). */
  lang?: BabelLang | null;
}) {
  return (
    <div className="card-rail" data-testid="card-rail" data-locked={locked}>
      {cards.flatMap((c) =>
        expandInstances(c).map((inst) => {
          const playable = inst.status === 'available' && !locked;
          return (
            <button
              key={`${c.id}#${inst.k}`}
              className="qcard"
              data-status={inst.status}
              data-vetoed={inst.vetoed || undefined}
              data-testid={inst.primary ? `card-${c.id}` : `card-${c.id}-i${inst.k}`}
              disabled={!playable}
              onClick={() => playable && onPick(c)}
            >
              <div className="qcard__top">
                <span>{bt(lang, 'seeker')}</span>
                {/* v29 BOLT-164 (Jake): "I don't think we display the cost on
                    the seeker cards during the games. This is critical
                    information that they need to decide whether it's worth
                    playing a card."
                    He is right, and this corner was FREE: it held the family
                    initial (R/D/S/P/A/L), which v24 removed on iOS because it
                    "told the player nothing they could act on" - the web just
                    kept rendering the dead letter. The cost is the opposite
                    kind of number: it is what the hider gets PAID for this
                    play, so it is the whole reason to hesitate. */}
                <span className="qcard__cost" data-testid={`card-cost-${c.id}`} title={`Costs the hider ${c.cost} card${c.cost === 1 ? '' : 's'}`}>
                  {c.cost}
                </span>
              </div>
              <div className="qcard__icon">
                <CardIcon family={c.family} id={c.id} />
              </div>
              <div className="qcard__name">{cardTitle(c, lang)}</div>
              <div className="qcard__data">{cardData(c, lang)}</div>
              <div className="qcard__state">{instanceState(c, inst.status, lang)}</div>
              <div className="qcard__rule" />
              {inst.vetoed && (
                // v14 BOLT-042: the full-card X on the DEAD copy — v15
                // BOLT-052 pins it to the most recently spent instance.
                <span className="qcard__veto" data-testid={`veto-x-${c.id}`} aria-hidden="true">
                  <svg viewBox="0 0 100 100" preserveAspectRatio="none">
                    <line x1="10" y1="10" x2="90" y2="90" />
                    <line x1="90" y1="10" x2="10" y2="90" />
                  </svg>
                </span>
              )}
            </button>
          );
        }),
      )}
    </div>
  );
}
